<template>
  <div
    v-if="slides.length"
    ref="rootRef"
    class="mall-rail"
    @mouseenter="paused = true"
    @mouseleave="resume"
    @focusin="paused = true"
    @focusout="resume"
  >
    <div class="mall-rail__stage">
      <component
        :is="isSuperAdmin ? 'button' : 'router-link'"
        v-for="(slide, i) in slides"
        :key="slide.code"
        v-bind="slideBind(slide)"
        class="mall-rail__card"
        :class="[`is-d${slide.design}`, { 'is-on': i === index }]"
        :aria-hidden="i === index ? undefined : 'true'"
        :tabindex="i === index ? undefined : -1"
        @click="onSlideClick($event, slide)"
      >
        <span class="mall-rail__media">
          <img :src="slide.image" alt="" />
        </span>
        <span class="mall-rail__copy">
          <span class="mall-rail__brand">함우리몰</span>
          <span class="mall-rail__name">{{ slide.name }}</span>
          <span class="mall-rail__price">{{ won(slide.price) }}</span>
        </span>
      </component>
    </div>
    <div v-if="slides.length > 1" class="mall-rail__dots">
      <button
        v-for="(slide, i) in slides"
        :key="slide.code"
        type="button"
        class="mall-rail__dot"
        :class="{ 'is-on': i === index }"
        :aria-label="`${slide.name} 보기`"
        :aria-current="i === index ? 'true' : undefined"
        @click="go(i)"
      />
    </div>

    <teleport to="body">
      <div
        v-if="selected"
        class="mall-rail-choice"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mall-rail-choice-title"
        @keydown.esc.prevent="closeChoice"
      >
        <button type="button" class="mall-rail-choice__catch" aria-label="닫기" @click="closeChoice" />
        <div class="mall-rail-choice__panel">
          <p class="mall-rail-choice__brand">함우리몰 웹배너</p>
          <h2 id="mall-rail-choice-title" class="mall-rail-choice__title">{{ selected.name }}</h2>
          <div class="mall-rail-choice__actions">
            <button type="button" class="mall-rail-choice__btn" @click="openProduct">
              해당 상품으로 이동하기
            </button>
            <button
              type="button"
              class="mall-rail-choice__btn mall-rail-choice__btn--prompt"
              :disabled="copying"
              @click="copyPrompt"
            >
              {{ copying ? '프롬프트를 작성하는 중' : '배너 상품 선정하여 이미지 새로 만드는 프롬프트 작성하기' }}
            </button>
          </div>
          <p v-if="copyNote" class="mall-rail-choice__note">{{ copyNote }}</p>
          <textarea
            v-if="promptText"
            class="mall-rail-choice__prompt"
            readonly
            :value="promptText"
          />
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { fetchLiveProduct, fetchTopSalesProducts, type MallTopSalesProduct } from '@/api/mallProduct'
import { mallProductPath } from '@/constants/mall'
import {
  bannerNeedsRefresh,
  buildTopSalesBannerPrompt,
  resolveTopSalesBanner,
  type TopSalesBannerBrief,
} from '@/constants/mallTopSalesBanners'
import { useAdminAuth } from '@/composables/useAdminAuth'

interface RailSlide {
  code: string
  name: string
  price: number
  image: string
  thumbnail: string
  to: string
  design: number
  rank: number
  category: string
  summary: string
  description: string
  unit: string
  origin: string
  maker: string
}

const SLIDE_COUNT = 10
const ROTATE_MS = 6000

const { isSuperAdmin, initAdminAuth } = useAdminAuth()
const rootRef = ref<HTMLElement | null>(null)
const slides = ref<RailSlide[]>([])
const index = ref(0)
const paused = ref(false)
const selected = ref<RailSlide | null>(null)
const copying = ref(false)
const copyNote = ref('')
const promptText = ref('')
let timer = 0

function won(value: number) {
  return `${value.toLocaleString('ko-KR')}원`
}

function slideBind(slide: RailSlide) {
  if (isSuperAdmin.value) return { type: 'button' }
  return { to: slide.to, target: '_blank', rel: 'noopener noreferrer' }
}

function toSlides(products: MallTopSalesProduct[]): RailSlide[] {
  return products.slice(0, SLIDE_COUNT).map((product, design) => ({
    code: product.code,
    name: product.name,
    price: product.price,
    image: resolveTopSalesBanner(product.code, product.thumbnail),
    thumbnail: product.thumbnail,
    to: mallProductPath(product.code),
    design,
    rank: product.rank || design + 1,
    category: product.category,
    summary: product.summary,
    description: product.description,
    unit: product.unit,
    origin: product.origin,
    maker: product.maker,
  }))
}

function onSlideClick(event: MouseEvent, slide: RailSlide) {
  if (!isSuperAdmin.value) return
  event.preventDefault()
  copyNote.value = ''
  promptText.value = ''
  selected.value = slide
  paused.value = true
}

function closeChoice() {
  selected.value = null
  copying.value = false
  const root = rootRef.value
  const hovering = Boolean(root?.matches(':hover'))
  const focused = Boolean(root && document.activeElement && root.contains(document.activeElement))
  paused.value = hovering || focused
}

function openProduct() {
  if (!selected.value) return
  window.open(selected.value.to, '_blank', 'noopener')
  closeChoice()
}

async function writeClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const area = document.createElement('textarea')
  area.value = text
  area.setAttribute('readonly', 'true')
  document.body.appendChild(area)
  area.select()
  const copied = document.execCommand('copy')
  area.remove()
  if (!copied) throw new Error('copy failed')
}

async function copyPrompt() {
  if (copying.value || !slides.value.length) return
  copying.value = true
  copyNote.value = ''
  try {
    const briefs = await Promise.all(slides.value.map((slide) => toBrief(slide)))
    const text = buildTopSalesBannerPrompt(briefs)
    promptText.value = text
    await writeClipboard(text)
    const refreshCount = briefs.filter((brief) => bannerNeedsRefresh(brief.code, brief.thumbnail)).length
    copyNote.value = refreshCount
      ? `프롬프트를 복사했습니다. 새로 만들 배너는 ${refreshCount}개입니다.`
      : '프롬프트를 복사했습니다. 현재 배너는 대표 이미지와 맞습니다.'
  } catch {
    copyNote.value = '클립보드 복사에 실패했습니다. 아래 내용을 직접 복사해 주세요.'
  } finally {
    copying.value = false
  }
}

async function toBrief(slide: RailSlide): Promise<TopSalesBannerBrief> {
  const detail = await fetchLiveProduct(slide.code).catch(() => null)
  return {
    rank: slide.rank,
    code: slide.code,
    name: detail?.name || slide.name,
    category: detail?.category || slide.category,
    summary: detail?.summary || slide.summary,
    description: detail?.description || slide.description,
    price: detail?.price ?? slide.price,
    unit: detail?.unit || slide.unit,
    origin: detail?.origin || slide.origin,
    maker: detail?.maker || slide.maker,
    thumbnail: detail?.thumbnail || slide.thumbnail,
    specs: detail?.specs || [],
  }
}

function tick() {
  if (paused.value || document.hidden || slides.value.length < 2) return
  index.value = (index.value + 1) % slides.value.length
}

function start() {
  stop()
  timer = window.setInterval(tick, ROTATE_MS)
}

function stop() {
  if (!timer) return
  window.clearInterval(timer)
  timer = 0
}

function resume(event: MouseEvent | FocusEvent) {
  const root = event.currentTarget
  const next = event.relatedTarget
  if (!(root instanceof HTMLElement)) return
  if (next instanceof Node && root.contains(next)) return
  if (selected.value) return
  window.setTimeout(() => {
    if (selected.value) return
    if (root.matches(':hover') || root.contains(document.activeElement)) return
    paused.value = false
  }, 0)
}

function go(next: number) {
  index.value = next
  start()
}

onMounted(async () => {
  void initAdminAuth()
  try {
    slides.value = toSlides(await fetchTopSalesProducts())
    if (slides.value.length > 1) {
      index.value = Math.floor(Math.random() * slides.value.length)
    }
  } catch {
    slides.value = []
  }
  start()
})

onUnmounted(stop)
</script>

<style lang="scss" src="./SectionMallBanner.scss"></style>
